import { useState, useEffect, useCallback } from "react";
import { ModeloGetUrl, ModeloPost } from "../../components/ModeloCrud"; // ajustá el path si es distinto
import { toast } from "react-toastify";
import {
  Box,
  Chip,
  CircularProgress,
  Divider,
  Drawer,
  IconButton,
  Tab,
  Tabs,
  Tooltip,
  Typography,
} from "@mui/material";
import { Close, Refresh, PersonOff } from "@mui/icons-material";
import PeopleAltIcon from "@mui/icons-material/PeopleAlt";
import { COLORS } from "../../pages/SalaEspera/salaEsperaUtils"; // mismo utils que ya tenés
import TicketCard from "../../pages/SalaEspera/TicketCard"; // mismo card que ya tenés

// ── helpers (igual que SalaEsperaPage) ──────────────────────
const PISO_LABEL = {
  PB: "Planta Baja",
  "01": "1er Piso",
  "02": "2do Piso",
  "03": "3er Piso",
};

function getClinica(t) {
  return t.totem_clinica ?? "Sin clínica";
}
function getPiso(t) {
  return t.consultorio_piso ?? "PB";
}
function getTabKey(t) {
  return `${getClinica(t)}|${getPiso(t)}`;
}

function getTabCombos(tickets) {
  const seen = new Set();
  const combos = [];
  tickets.forEach((t) => {
    const key = getTabKey(t);
    if (!seen.has(key)) {
      seen.add(key);
      combos.push({ key, clinica: getClinica(t), piso: getPiso(t) });
    }
  });
  return combos.sort((a, b) => {
    const pa = a.piso === "PB" ? "0" : a.piso;
    const pb = b.piso === "PB" ? "0" : b.piso;
    return a.clinica.localeCompare(b.clinica) || pa.localeCompare(pb);
  });
}

// ────────────────────────────────────────────────────────────
export default function SalaEsperaDrawer({
  open,
  onClose,
  onCountChange,
  centroId,
}) {
  const [tickets, setTickets] = useState([]);
  const [loading, setLoading] = useState(false);
  const [llamandoId, setLlamandoId] = useState(null);
  const [lastUpdate, setLastUpdate] = useState(null);
  const [activeTab, setActiveTab] = useState(null);

  const fetchTickets = useCallback(async () => {
    setLoading(true);
    try {
      const params = centroId ? `centro_id=${centroId}` : null;
      const result = await ModeloGetUrl(
        "tickets-atencion",
        null,
        "dia",
        params,
      );

      if (result.error === false) {
        const lista = result.listado ?? [];
        setTickets(lista);
        onCountChange?.(lista.length);
        setActiveTab((prev) => {
          if (prev) return prev;
          const combos = getTabCombos(lista);
          return combos.length > 0 ? combos[0].key : null;
        });
      } else {
        toast.error(result.errormessage ?? "Error al cargar tickets");
      }
    } catch {
      toast.error("Error de conexión");
    } finally {
      setLoading(false);
      setLastUpdate(new Date());
    }
  }, [centroId, onCountChange]);

  // Fetch inicial + polling 30s
  useEffect(() => {
    if (!open) return; // 👈 solo cuando está abierto
    fetchTickets();
    const interval = setInterval(fetchTickets, 30000);
    return () => clearInterval(interval);
  }, [fetchTickets, open]); // 👈 open como dependencia

  const handleLlamar = async (id) => {
    setLlamandoId(id);
    try {
      const result = await ModeloPost("tickets-atencion", id, "llamar", {});
      if (result.error === false) {
        toast.success("Paciente llamado");
        await fetchTickets();
      } else {
        toast.error(result.errormessage ?? "Error al llamar paciente");
      }
    } catch {
      toast.error("Error de conexión");
    } finally {
      setLlamandoId(null);
    }
  };

  const combos = getTabCombos(tickets);
  const ticketsFiltrados = activeTab
    ? tickets.filter((t) => getTabKey(t) === activeTab)
    : [];
  const countForCombo = (key) =>
    tickets.filter((t) => getTabKey(t) === key).length;

  return (
    <Drawer
      anchor="right"
      open={open}
      onClose={onClose}
      PaperProps={{
        sx: {
          width: { xs: "92vw", sm: 720 },
          background: COLORS.pageBg,
          display: "flex",
          flexDirection: "column",
        },
      }}
    >
      {/* ── Header ── */}
      <Box
        sx={{
          background: "linear-gradient(135deg, #1976d2 0%, #1565c0 100%)",
          color: "white",
          px: 2,
          pt: 2,
          pb: 2,
          flexShrink: 0,
        }}
      >
        {/* Fila título */}
        <Box
          display="flex"
          alignItems="center"
          justifyContent="space-between"
          mb={0.5}
        >
          <Box display="flex" alignItems="center" gap={1}>
            <PeopleAltIcon sx={{ fontSize: 22 }} />
            <Box>
              <Typography variant="h6" fontWeight={700} lineHeight={1}>
                Sala de Espera
              </Typography>
              <Typography variant="caption" sx={{ opacity: 0.75 }}>
                {lastUpdate
                  ? `Actualizado ${lastUpdate.toLocaleTimeString("es-AR", {
                      hour: "2-digit",
                      minute: "2-digit",
                    })}`
                  : "Cargando..."}
              </Typography>
            </Box>
          </Box>

          <Box display="flex" alignItems="center" gap={0.5}>
            <Chip
              label={`${tickets.length} en espera`}
              size="small"
              sx={{
                background: "rgba(255,255,255,0.2)",
                color: "white",
                fontWeight: 600,
              }}
            />
            <Tooltip title="Actualizar">
              <span>
                <IconButton
                  size="small"
                  onClick={fetchTickets}
                  disabled={loading}
                  sx={{ color: "white" }}
                >
                  {loading ? (
                    <CircularProgress size={18} sx={{ color: "white" }} />
                  ) : (
                    <Refresh fontSize="small" />
                  )}
                </IconButton>
              </span>
            </Tooltip>
            <IconButton size="small" onClick={onClose} sx={{ color: "white" }}>
              <Close fontSize="small" />
            </IconButton>
          </Box>
        </Box>

        {/* Tabs — igual que SalaEsperaPage */}
        {!loading && combos.length > 0 && (
          <Tabs
            value={activeTab ?? false}
            onChange={(_, v) => setActiveTab(v)}
            variant="scrollable"
            scrollButtons="auto"
            sx={{
              minHeight: 40,
              "& .MuiTab-root": {
                minHeight: 40,
                py: 0,
                textTransform: "none",
                fontSize: 12,
                color: "rgba(255,255,255,0.7)",
              },
              "& .Mui-selected": { color: "white !important" },
              "& .MuiTabs-indicator": { backgroundColor: "#fdd835" },
            }}
          >
            {combos.map(({ key, clinica, piso }) => (
              <Tab
                key={key}
                value={key}
                label={
                  <Box display="flex" alignItems="center" gap={0.75}>
                    <span>{`${clinica} · ${PISO_LABEL[piso] ?? piso}`}</span>
                    <Chip
                      label={countForCombo(key)}
                      size="small"
                      sx={{
                        height: 16,
                        fontSize: 10,
                        background:
                          activeTab === key ? "white" : "rgba(255,255,255,0.2)",
                        color: activeTab === key ? "#9c27b0" : "white",
                        "& .MuiChip-label": { px: 0.6 },
                      }}
                    />
                  </Box>
                }
              />
            ))}
          </Tabs>
        )}
      </Box>

      {/* ── Contenido scrolleable ── */}
      <Box sx={{ flex: 1, overflowY: "auto", p: 2 }}>
        {!loading && tickets.length === 0 && (
          <Box textAlign="center" mt={6}>
            <PersonOff sx={{ fontSize: 56, color: COLORS.info, mb: 1 }} />
            <Typography color="text.secondary">
              No hay pacientes en espera hoy
            </Typography>
          </Box>
        )}

        {!loading && tickets.length > 0 && ticketsFiltrados.length === 0 && (
          <Box textAlign="center" mt={6}>
            <PersonOff sx={{ fontSize: 48, color: COLORS.info, mb: 1 }} />
            <Typography variant="body2" color="text.secondary">
              No hay pacientes en esta sala
            </Typography>
          </Box>
        )}

        {ticketsFiltrados.map((t) => (
          <TicketCard
            key={t.id}
            ticket={t}
            onLlamar={handleLlamar}
            llamando={llamandoId === t.id}
          />
        ))}
      </Box>
    </Drawer>
  );
}
